import React, { useCallback, useEffect, useState } from 'react';
import {
  PageHeader,
  Card,
  StatGrid,
  StatCard,
  DataTable,
  Badge,
  Button,
  SearchInput,
  Tabs,
  TabPanel,
  Toast,
  EmptyState,
  ConfirmModal,
} from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import {
  getCategorisationOverview,
  listCategorisationRules,
  updateCategorisationRule,
  deleteCategorisationRule,
  seedCategorisationRules,
  previewCategorisation,
  createCategorisationJob,
  getCategorisationLog,
  revertCategorisationJob,
  syncBcStoreCategories,
  subscribeToEnrichJobStream,
} from '../../services/pimApi';
import './pim.css';

const JOB_STATUS_VARIANTS = {
  pending: 'warning',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

function CodeList({ codes }) {
  if (!codes || codes.length === 0) return <span style={{ color: '#999' }}>—</span>;
  return (
    <span className="ac-codes">
      {codes.map((c) => (
        <code key={c} style={{ display: 'inline-block', margin: '1px 4px 1px 0' }}>{c}</code>
      ))}
    </span>
  );
}

function JobProgressCard({ jobId, progress }) {
  const c = progress?.counters || {};
  return (
    <Card style={{ marginBottom: 12 }}>
      <p style={{ margin: '0 0 8px' }}>
        Categorisation job #{jobId}: <strong>{progress?.status || 'running'}</strong>
      </p>
      <p style={{ margin: 0, fontSize: 13, color: '#667' }}>
        {typeof c.processed === 'number' ? `${c.processed}/${c.eligible ?? '?'} processed` : 'Starting…'}
        {typeof c.rule_matched === 'number' ? ` · ${c.rule_matched} by rules` : ''}
        {typeof c.ai_classified === 'number' && c.ai_classified ? ` · ${c.ai_classified} by AI` : ''}
        {typeof c.updated === 'number' ? ` · ${c.updated} changed` : ''}
        {typeof c.errors === 'number' && c.errors ? ` · ${c.errors} errors` : ''}
      </p>
    </Card>
  );
}

export default function AutoCategorisation() {
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  // Overview
  const [overview, setOverview] = useState(null);

  // Job runner
  const [jobFilter, setJobFilter] = useState('web_enabled');
  const [jobDryRun, setJobDryRun] = useState(true);
  const [jobAi, setJobAi] = useState(true);
  const [job, setJob] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);

  // Rules
  const [rules, setRules] = useState([]);
  const [rulesTotal, setRulesTotal] = useState(0);
  const [rulesQ, setRulesQ] = useState('');
  const [rulesOffset, setRulesOffset] = useState(0);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Log / provenance
  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logSkuDraft, setLogSkuDraft] = useState('');
  const logSku = useDebouncedValue(logSkuDraft);
  const [logJobId, setLogJobId] = useState('');
  const [logOffset, setLogOffset] = useState(0);
  const [logLoading, setLogLoading] = useState(false);

  // Preview
  const [previewSku, setPreviewSku] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // BC sync
  const [bcStore, setBcStore] = useState('AU');
  const [bcEnv, setBcEnv] = useState('staging');
  const [bcResult, setBcResult] = useState(null);

  const pageSize = 50;

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await getCategorisationOverview());
    } catch (e) {
      setToast({ message: 'Failed to load overview', error: true });
    }
  }, []);

  const loadRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const params = { limit: pageSize, offset: rulesOffset };
      if (rulesQ) params.q = rulesQ;
      const data = await listCategorisationRules(params);
      setRules(data.items);
      setRulesTotal(data.total);
    } catch (e) {
      setToast({ message: 'Failed to load rules', error: true });
    } finally {
      setRulesLoading(false);
    }
  }, [rulesQ, rulesOffset]);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = { limit: pageSize, offset: logOffset };
      if (logSku) params.sku = logSku;
      if (logJobId) params.job_id = logJobId;
      const data = await getCategorisationLog(params);
      setLog(data.items);
      setLogTotal(data.total);
    } catch (e) {
      setToast({ message: 'Failed to load run log', error: true });
    } finally {
      setLogLoading(false);
    }
  }, [logSku, logJobId, logOffset]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === 'rules') loadRules(); }, [tab, loadRules]);
  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

  const startJob = async () => {
    setBusy(true);
    try {
      const created = await createCategorisationJob({
        filter: jobFilter,
        dry_run: jobDryRun,
        ai_enabled: jobAi,
      });
      setJob(created.id);
      setJobProgress(null);
      subscribeToEnrichJobStream(
        created.id,
        (p) => setJobProgress(p),
        () => {
          loadOverview();
          setToast({ message: `Job #${created.id} finished` });
        }
      );
      setToast({ message: `Categorisation job #${created.id} enqueued${jobDryRun ? ' (dry run)' : ''}` });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Failed to start job', error: true });
    } finally {
      setBusy(false);
    }
  };

  const doRevert = async () => {
    const target = revertTarget;
    setRevertTarget(null);
    setBusy(true);
    try {
      const res = await revertCategorisationJob(target);
      setToast({
        message: `Reverted job #${target}: ${res.reverted} product(s) restored, ${res.skipped_human} skipped (human-edited)`,
      });
      loadOverview();
      if (tab === 'log') loadLog();
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Revert failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const doSeedRules = async () => {
    setBusy(true);
    try {
      const res = await seedCategorisationRules();
      setToast({ message: `Rules reseeded: ${res.inserted} inserted, ${res.deleted} replaced` });
      loadRules();
      loadOverview();
    } catch (e) {
      setToast({ message: 'Rule seeding failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const doPreview = async () => {
    if (!previewSku.trim()) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      setPreview(await previewCategorisation(previewSku.trim()));
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Preview failed', error: true });
    } finally {
      setPreviewLoading(false);
    }
  };

  const doBcSync = async (dryRun) => {
    setBusy(true);
    setBcResult(null);
    try {
      const res = await syncBcStoreCategories({
        store_code: bcStore,
        environment: bcEnv,
        dry_run: dryRun,
      });
      setBcResult(res);
      setToast({
        message: `BC category sync ${dryRun ? '(dry run) ' : ''}done: ${res.created} created, ${res.adopted} adopted, ${res.unchanged} unchanged`,
        error: res.errors > 0,
      });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'BC category sync failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      await updateCategorisationRule(rule.id, { enabled: !rule.enabled });
      loadRules();
    } catch (e) {
      setToast({ message: 'Failed to update rule', error: true });
    }
  };

  const removeRule = async (rule) => {
    try {
      await deleteCategorisationRule(rule.id);
      setToast({ message: `Rule #${rule.id} deleted` });
      loadRules();
    } catch (e) {
      setToast({ message: 'Failed to delete rule', error: true });
    }
  };

  const totals = overview?.totals || {};
  const coveragePct = totals.active_web
    ? Math.round((100 * (totals.active_web - (totals.uncategorised_web || 0))) / totals.active_web)
    : 0;

  return (
    <div className="pim-page">
      <PageHeader
        title="Auto-Categorisation"
        sub="Store-tree categorisation: deterministic Epicor shortcode rules first, Gemini AI fallback. Human edits always win over auto runs."
      />
      {toast && <Toast message={toast.message} error={toast.error} />}

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview & Jobs' },
          { id: 'rules', label: 'Rules' },
          { id: 'log', label: 'Run Log' },
          { id: 'bc', label: 'BigCommerce' },
        ]}
        active={tab}
        onChange={setTab}
      />

      <TabPanel id="overview" active={tab}>
        <StatGrid>
          <StatCard value={totals.active_products ?? '—'} label="Active products" />
          <StatCard value={totals.active_web ?? '—'} label="Active + web enabled" />
          <StatCard value={totals.categorised ?? '—'} label="With store categories" />
          <StatCard
            value={`${coveragePct}%`}
            label="Web coverage"
            sub={`${totals.uncategorised_web ?? '—'} uncategorised`}
            tone={coveragePct >= 95 ? 'success' : coveragePct >= 70 ? 'warning' : 'danger'}
          />
        </StatGrid>

        <Card title="Products per division" style={{ marginTop: 12 }}>
          <DataTable
            columns={[
              { key: 'root_label', header: 'Division' },
              { key: 'root_code', header: 'Code', render: (r) => <code>{r.root_code}</code> },
              { key: 'products', header: 'Products', align: 'right' },
            ]}
            rows={overview?.by_division || []}
            rowKey={(r) => r.root_code}
            loading={!overview}
          />
        </Card>

        <Card
          title="Run categorisation"
          desc="Rules apply with confidence 1.0; products with no rule match go to the AI classifier. Dry runs only write the report log."
          style={{ marginTop: 12 }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label>
              Scope{' '}
              <select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
                <option value="web_enabled">Active + web enabled</option>
                <option value="all_active">All active</option>
                <option value="uncategorised">Uncategorised only</option>
              </select>
            </label>
            <label>
              <input type="checkbox" checked={jobDryRun} onChange={(e) => setJobDryRun(e.target.checked)} /> Dry run
            </label>
            <label>
              <input type="checkbox" checked={jobAi} onChange={(e) => setJobAi(e.target.checked)} /> AI fallback
            </label>
            <Button onClick={startJob} disabled={busy}>
              {jobDryRun ? 'Run dry run' : 'Run & apply'}
            </Button>
          </div>
          {job && <div style={{ marginTop: 12 }}><JobProgressCard jobId={job} progress={jobProgress} /></div>}
        </Card>

        <Card title="Recent jobs" style={{ marginTop: 12 }}>
          <DataTable
            columns={[
              { key: 'id', header: '#', width: 60 },
              {
                key: 'status',
                header: 'Status',
                render: (r) => (
                  <Badge variant={JOB_STATUS_VARIANTS[r.status] || 'neutral'}>
                    {r.status}{r.dry_run ? ' · dry' : ''}
                  </Badge>
                ),
              },
              { key: 'created_by', header: 'By' },
              {
                key: 'counters',
                header: 'Result',
                render: (r) => {
                  const c = r.counters || {};
                  return `${c.processed ?? 0}/${c.eligible ?? 0} · rules ${c.rule_matched ?? 0} · AI ${c.ai_classified ?? 0} · changed ${c.updated ?? 0}`;
                },
              },
              {
                key: 'created_at',
                header: 'Started',
                render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—'),
              },
              {
                key: 'actions',
                header: '',
                render: (r) =>
                  r.status === 'succeeded' && !r.dry_run ? (
                    <Button size="sm" variant="ghost" onClick={() => setRevertTarget(r.id)} disabled={busy}>
                      Revert
                    </Button>
                  ) : null,
              },
            ]}
            rows={overview?.recent_jobs || []}
            rowKey={(r) => r.id}
            loading={!overview}
            empty={<EmptyState title="No categorisation jobs yet" body="Run a dry run above to see the proposed distribution." />}
          />
        </Card>

        <Card title="Preview a SKU" desc="Classify one product (rules, then AI) without writing anything." style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SearchInput value={previewSku} onChange={setPreviewSku} placeholder="SKU…" />
            <Button onClick={doPreview} disabled={previewLoading || !previewSku.trim()}>
              {previewLoading ? 'Classifying…' : 'Preview'}
            </Button>
          </div>
          {preview && (
            <div style={{ marginTop: 12, fontSize: 14 }}>
              <p style={{ margin: '4px 0' }}>
                <strong>{preview.sku}</strong> — {preview.product?.name}
                {' '}
                <Badge variant={preview.matched_by === 'rules' ? 'success' : preview.matched_by === 'ai' ? 'info' : 'danger'}>
                  {preview.matched_by || 'none'}
                </Badge>
                {typeof preview.confidence === 'number' && ` · confidence ${preview.confidence.toFixed(2)}`}
              </p>
              <p style={{ margin: '4px 0' }}>Proposed: <CodeList codes={preview.codes} /></p>
              <p style={{ margin: '4px 0' }}>Current: <CodeList codes={preview.current_codes} /></p>
              {preview.rationale && <p style={{ margin: '4px 0', color: '#667' }}>{preview.rationale}</p>}
            </div>
          )}
        </Card>
      </TabPanel>

      <TabPanel id="rules" active={tab}>
        <Card
          title="Categorisation rules"
          desc="Union semantics: every matching rule contributes its categories. Seeded rules regenerate from the Phocas product-group mapping; manual rules are kept."
          actions={<Button onClick={doSeedRules} disabled={busy}>Reseed from Phocas</Button>}
        >
          <div style={{ marginBottom: 8 }}>
            <SearchInput
              value={rulesQ}
              onChange={(v) => { setRulesQ(v); setRulesOffset(0); }}
              placeholder="Search rules (name, tag code, target)…"
            />
          </div>
          <DataTable
            columns={[
              { key: 'id', header: '#', width: 60 },
              { key: 'name', header: 'Rule' },
              {
                key: 'predicate',
                header: 'Predicate',
                render: (r) => (
                  <span style={{ fontSize: 12 }}>
                    {r.match_tag_code && <span>tag=<code>{r.match_tag_code}</code> </span>}
                    {r.match_prod_code && <span>prod=<code>{r.match_prod_code}</code> </span>}
                    {r.match_class_id && <span>class=<code>{r.match_class_id}</code> </span>}
                    {r.match_web_category && <span>webcat=<code>{r.match_web_category}</code> </span>}
                    {r.match_name_regex && <span>name~<code>{r.match_name_regex}</code></span>}
                  </span>
                ),
              },
              { key: 'target_category_codes', header: 'Targets', render: (r) => <CodeList codes={r.target_category_codes} /> },
              { key: 'priority', header: 'Priority', align: 'right' },
              {
                key: 'source',
                header: 'Source',
                render: (r) => <Badge variant={r.source === 'manual' ? 'info' : 'neutral'}>{r.source}</Badge>,
              },
              {
                key: 'enabled',
                header: 'Enabled',
                render: (r) => (
                  <Button size="sm" variant="ghost" onClick={() => toggleRule(r)}>
                    <Badge variant={r.enabled ? 'success' : 'neutral'}>{r.enabled ? 'on' : 'off'}</Badge>
                  </Button>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (r) => (
                  <Button size="sm" variant="ghost" onClick={() => removeRule(r)}>Delete</Button>
                ),
              },
            ]}
            rows={rules}
            rowKey={(r) => r.id}
            loading={rulesLoading}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <Button size="sm" variant="ghost" disabled={rulesOffset === 0} onClick={() => setRulesOffset(Math.max(0, rulesOffset - pageSize))}>Prev</Button>
            <span style={{ fontSize: 13 }}>{rulesOffset + 1}–{Math.min(rulesOffset + pageSize, rulesTotal)} of {rulesTotal}</span>
            <Button size="sm" variant="ghost" disabled={rulesOffset + pageSize >= rulesTotal} onClick={() => setRulesOffset(rulesOffset + pageSize)}>Next</Button>
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="log" active={tab}>
        <Card title="Run log" desc="Every change (and dry-run proposal) with before/after codes and provenance.">
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <SearchInput
              value={logSkuDraft}
              onChange={(v) => { setLogSkuDraft(v); setLogOffset(0); }}
              placeholder="Filter by SKU — wildcards * and ?"
            />
            <input
              type="number"
              value={logJobId}
              onChange={(e) => { setLogJobId(e.target.value); setLogOffset(0); }}
              placeholder="Job #"
              style={{ width: 90 }}
            />
          </div>
          <DataTable
            columns={[
              { key: 'job_id', header: 'Job', width: 60, render: (r) => r.job_id ?? '—' },
              { key: 'sku', header: 'SKU' },
              { key: 'prior_codes', header: 'Before', render: (r) => <CodeList codes={r.prior_codes} /> },
              { key: 'new_codes', header: 'After', render: (r) => <CodeList codes={r.new_codes} /> },
              {
                key: 'provenance',
                header: 'Provenance',
                render: (r) => {
                  const sources = [...new Set(Object.values(r.provenance || {}).map((p) => p.source))];
                  return sources.length ? sources.map((s) => (
                    <Badge key={s} variant={s === 'ai' ? 'info' : 'neutral'}>{s}</Badge>
                  )) : '—';
                },
              },
              {
                key: 'applied',
                header: 'Status',
                render: (r) => (
                  <Badge variant={r.reverted_at ? 'warning' : r.applied ? 'success' : 'neutral'}>
                    {r.reverted_at ? 'reverted' : r.applied ? 'applied' : 'dry run'}
                  </Badge>
                ),
              },
              {
                key: 'created_at',
                header: 'When',
                render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—'),
              },
            ]}
            rows={log}
            rowKey={(r) => r.id}
            loading={logLoading}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <Button size="sm" variant="ghost" disabled={logOffset === 0} onClick={() => setLogOffset(Math.max(0, logOffset - pageSize))}>Prev</Button>
            <span style={{ fontSize: 13 }}>{logOffset + 1}–{Math.min(logOffset + pageSize, logTotal)} of {logTotal}</span>
            <Button size="sm" variant="ghost" disabled={logOffset + pageSize >= logTotal} onClick={() => setLogOffset(logOffset + pageSize)}>Next</Button>
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="bc" active={tab}>
        <Card
          title="BigCommerce category sync"
          desc="Creates/updates BC categories mirroring the store tree and fills the category-id mappings. The product category push switches to the store tree via BC_CATEGORY_SOURCE=store after review."
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label>
              Store{' '}
              <select value={bcStore} onChange={(e) => setBcStore(e.target.value)}>
                <option value="AU">AU</option>
                <option value="NZ">NZ</option>
              </select>
            </label>
            <label>
              Environment{' '}
              <select value={bcEnv} onChange={(e) => setBcEnv(e.target.value)}>
                <option value="staging">staging</option>
                <option value="prod">prod</option>
              </select>
            </label>
            <Button onClick={() => doBcSync(true)} disabled={busy}>Dry run</Button>
            <Button onClick={() => doBcSync(false)} disabled={busy} variant="primary">Create categories</Button>
          </div>
          {bcResult && (
            <p style={{ marginTop: 12, fontSize: 14 }}>
              {bcResult.nodes} node(s) · {bcResult.created} created · {bcResult.adopted} adopted ·{' '}
              {bcResult.unchanged} unchanged · {bcResult.skipped} skipped · {bcResult.errors} errors
              {bcResult.dry_run ? ' (dry run)' : ''}
            </p>
          )}
        </Card>
      </TabPanel>

      {revertTarget != null && (
        <ConfirmModal
          title={`Revert job #${revertTarget}?`}
          body="Auto-assigned categories from this run are restored to their previous state. Products a human has edited since the run are skipped."
          confirmLabel="Revert"
          onConfirm={doRevert}
          onCancel={() => setRevertTarget(null)}
        />
      )}
    </div>
  );
}
